import { api } from '@/lib/axios';

export interface RegisterClientDto {
  name: string;
  email: string;
  password: string;
  phone?: string;
  document?: string;
}

export interface LoginClientDto {
  email: string;
  password: string;
}

export interface ClientAuthResponse {
  client: {
    id: string;
    name: string;
    email: string;
    phone?: string;
    document?: string;
    categoryId?: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

export interface ClientProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  document?: string;
  birthDate?: string;
  categoryId?: string;
  category?: {
    id: string;
    name: string;
    discount?: number;
  };
  zipCode?: string;
  address?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
}

export interface UpdateClientProfileDto {
  name?: string;
  phone?: string;
  document?: string;
  birthDate?: string;
  zipCode?: string;
  address?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
}

export interface ForgotPasswordDto {
  email: string;
}

export interface ResetPasswordDto {
  token: string;
  password: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export const clientAuthService = {
  async register(data: RegisterClientDto): Promise<ClientAuthResponse> {
    const response = await api.post<ClientAuthResponse>('/clients/register', data);

    // Store tokens in localStorage
    if (response.data.tokens) {
      localStorage.setItem('client_access_token', response.data.tokens.accessToken);
      localStorage.setItem('client_refresh_token', response.data.tokens.refreshToken);
    }

    return response.data;
  },

  async login(data: LoginClientDto): Promise<ClientAuthResponse> {
    const response = await api.post<ClientAuthResponse>('/clients/login', data);

    // Store tokens in localStorage
    if (response.data.tokens) {
      localStorage.setItem('client_access_token', response.data.tokens.accessToken);
      localStorage.setItem('client_refresh_token', response.data.tokens.refreshToken);
    }

    return response.data;
  },

  async getProfile(): Promise<ClientProfile> {
    const token = localStorage.getItem('client_access_token');
    const response = await api.get<ClientProfile>('/clients/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  async updateProfile(data: UpdateClientProfileDto): Promise<ClientProfile> {
    const token = localStorage.getItem('client_access_token');
    const response = await api.patch<ClientProfile>('/clients/me', data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  logout() {
    localStorage.removeItem('client_access_token');
    localStorage.removeItem('client_refresh_token');
  },

  getAccessToken(): string | null {
    return localStorage.getItem('client_access_token');
  },

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  },

  async forgotPassword(data: ForgotPasswordDto): Promise<ForgotPasswordResponse> {
    const response = await api.post<ForgotPasswordResponse>('/clients/forgot-password', data);
    return response.data;
  },

  async resetPassword(data: ResetPasswordDto): Promise<ResetPasswordResponse> {
    const response = await api.post<ResetPasswordResponse>('/clients/reset-password', data);
    return response.data;
  },
};
